import type { ReactNode } from "react";

import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { BillingStripeNotificationModal } from "@v2/features/billing/modals/BillingStripeNotificationModal";
import { CancelPlanModal } from "@v2/features/billing/modals/CancelPlanModal";
import { PlanChangeConfirmationModal } from "@v2/features/billing/modals/PlanChangeConfirmationModal";
import { useResetFiles } from "@v2/features/debug/useResetFiles";
import { useModal } from "@v2/store/modalStore";
import { TriangleAlert } from "lucide-react";
import {
  ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME,
  ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME,
} from "schemas/addOns";

const ActionCard: React.FC<{
  title: string;
  description: string;
  action: ReactNode;
}> = ({ title, description, action }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5">
    <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
      <span className="text-xs font-medium text-default">{title}</span>
      <span className="text-xs text-muted">{description}</span>
    </div>
    <div className="shrink-0">{action}</div>
  </div>
);

const ResetFilesAction: React.FC = () => {
  const { resetFiles, isLoadingFilesReset } = useResetFiles();
  return (
    <ActionCard
      title="Reset Files (V2)"
      description="Resets all project files to their initial state. This cannot be undone."
      action={
        <Button
          size="sm"
          variant="destructive"
          onClick={() => void resetFiles()}
          loading={isLoadingFilesReset}
        >
          Reset
        </Button>
      }
    />
  );
};

const ErrorModalAction: React.FC = () => {
  const { openModal } = useModal();
  return (
    <ActionCard
      title="Full Page Error Modal"
      description="Opens a test error modal to preview the error state UI"
      action={
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            openModal({
              type: "fullPageErrorModal",
              props: {
                details: {
                  header: "Test Error Modal",
                  subheader: "Debug Test",
                  message:
                    "This is a test error modal opened from debug panel.",
                },
              },
            });
          }}
        >
          Trigger
        </Button>
      }
    />
  );
};

const BillingModalsSection: React.FC = () => {
  const [planChangeModalOpen, setPlanChangeModalOpen] = React.useState(false);
  const [
    billingStripeNotificationModalOpen,
    setBillingStripeNotificationModalOpen,
  ] = React.useState(false);
  const [cancelPlanModalOpen, setCancelPlanModalOpen] = React.useState(false);

  return (
    <>
      <ActionCard
        title="Billing Stripe Notification"
        description="Preview the Stripe billing notification modal flow"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setBillingStripeNotificationModalOpen(true)}
          >
            Trigger
          </Button>
        }
      />
      <ActionCard
        title="Cancel Plan"
        description="Preview the plan cancellation confirmation modal"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setCancelPlanModalOpen(true)}
          >
            Trigger
          </Button>
        }
      />
      <ActionCard
        title="Plan Change Confirmation"
        description="Preview the plan upgrade/downgrade confirmation modal"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setPlanChangeModalOpen(true)}
          >
            Trigger
          </Button>
        }
      />
      <PlanChangeConfirmationModal
        open={planChangeModalOpen}
        onOpenChange={setPlanChangeModalOpen}
        newTier="starter"
        newBillingInterval="monthly"
        addOns={[
          {
            type: ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME,
            quantity: 2,
          },
          {
            type: ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME,
            quantity: 2,
          },
        ]}
      />
      <BillingStripeNotificationModal
        open={billingStripeNotificationModalOpen}
        onOpenChange={setBillingStripeNotificationModalOpen}
        buttonText="Continue to Stripe"
        onConfirm={() => {
          console.log("Billing Stripe Notification Modal confirmed");
        }}
      />
      <CancelPlanModal
        open={cancelPlanModalOpen}
        onOpenChange={setCancelPlanModalOpen}
        source="direct.billingDashboard"
      />
    </>
  );
};

export const MiscellaneousTab: React.FC = () => {
  return (
    <>
      <div className="flex items-center gap-2 rounded-lg border border-amber-border bg-amber-soft px-2.5 py-1.5">
        <TriangleAlert className="size-3 shrink-0 text-amber" />
        <span className="text-xs text-amber">
          Destructive actions cannot be undone.
        </span>
      </div>
      <ResetFilesAction />
      <ErrorModalAction />
      <BillingModalsSection />
    </>
  );
};
